// @flow
import React from 'react'
import { useFormContext, Controller } from 'react-hook-form'

import { Button, Flex, List, NoSSR, colors } from 'components/common'
import { ControllerError } from 'components/common/form'
import * as Modal from './modals'
import { reorder } from '~/core/utils/functions'
import { useGlobalDispatch } from '~/core/context'
import { useI18n } from '~/core/i18n'
import { useListSortStyles } from './styles'

import type { DynamicFieldProps } from './modals'
import type { Mapping } from './types.flow'

type MapperListWrapperProps = {
  value?: Mapping[],
  label?: string,
  customItemContent?: (
    mapping: Mapping,
    index: number
  ) => Array<{
    label: string,
    value: string,
    color?: string
  }>,
  customTexts?: {
    addMapping?: string,
    modalTitle?: string,
    key?: string,
    value?: string
  },
  customFields?: DynamicFieldProps[],
  isSortable?: boolean,
  isRemovable?: (value: Mapping) => boolean | boolean,
  onChange?: (mappings: Mapping[]) => void
}

const MapperListWrapper = ({
  value: mappings = [],
  label,
  customFields,
  customTexts,
  isSortable,
  isRemovable = true,
  customItemContent,
  onChange = () => {}
}: MapperListWrapperProps) => {
  const [msg] = useI18n()
  const dispatch = useGlobalDispatch()
  const classes = useListSortStyles()

  const handleSubmit = (id?: string, data: Mapping) => {
    const newValue = id ? mappings.map(mapping => (mapping.id === id ? data : mapping)) : [...mappings, data]
    dispatch({
      type: 'modal/close'
    })
    onChange(newValue)
  }

  const handleSort = ({ oldIndex, newIndex }) => {
    const newValue = reorder(mappings, oldIndex, newIndex)
    onChange(newValue)
  }

  const handleEditMapping = (id?: string) => {
    dispatch({
      type: 'modal/open',
      payload: {
        title: customTexts?.modalTitle || msg('common.controllers.mappings.editMapping'),
        content: (
          <Modal.EditMapping
            customFields={customFields}
            customTexts={customTexts}
            mapping={id ? mappings.find(mapping => mapping.id === id) : undefined}
            onSubmit={data => handleSubmit(id, data)}
          />
        )
      }
    })
  }

  const handleRemoveMapping = (id: string) => {
    const newVariables = mappings.filter(variable => variable.id !== id)
    onChange(newVariables)
  }

  return (
    <>
      <NoSSR requireFunc={() => require('react-sortable-hoc')}>
        {({ SortableContainer, SortableElement }) => {
          const SortableListItem = SortableElement(({ mapping, index }: { mapping: Mapping, index: number }) => (
            <List.Item
              content={
                typeof customItemContent === 'function'
                  ? customItemContent(mapping, index)
                  : [
                      {
                        label: customTexts?.key || msg('common.controllers.mappings.key'),
                        value: mapping.name,
                        color: colors.blue500
                      },
                      { label: customTexts?.value || msg('common.controllers.mappings.value'), value: mapping.value }
                    ]
              }
              id={mapping.id}
              isEditable
              isRemovable={typeof isRemovable === 'function' ? isRemovable(mapping) : isRemovable}
              label={mapping.name}
              onEdit={() => handleEditMapping(mapping.id)}
              onRemove={() => handleRemoveMapping(mapping.id)}
            />
          ))
          const SortableList = SortableContainer(() => (
            <List label={label}>
              {mappings.map((mapping, index) => (
                <SortableListItem key={mapping.id} index={index} mapping={mapping} />
              ))}
            </List>
          ))

          return (
            <SortableList
              helperClass={classes.helperClass}
              lockAxis="y"
              shouldCancelStart={({ target }) => {
                if (isSortable) {
                  return [...target.classList].includes('icon')
                } else {
                  return true
                }
              }}
              onSortEnd={handleSort}
            />
          )
        }}
      </NoSSR>
      <Flex>
        <Button iconPosition="left" id="add-output-btn" xIcon="add" xStyle="flat" onClick={() => handleEditMapping()}>
          {customTexts?.addMapping || msg('common.button.add')}
        </Button>
      </Flex>
    </>
  )
}

type MapperControllerProps = {
  name: string,
  label?: string,
  customFields?: DynamicFieldProps[],
  rules?: { [key: string]: any },
  customTexts?: {
    addMapping?: string,
    modalTitle?: string,
    key?: string,
    value?: string
  },
  customItemContent?: (
    mapping: Mapping,
    index: number
  ) => Array<{
    label: string,
    value: string,
    color?: string
  }>
}

const GenericMapperController = ({ name, rules, ...props }: MapperControllerProps) => {
  const { errors } = useFormContext()
  return (
    <Flex column>
      <Controller
        name={name}
        render={({ field: { value, onChange } }) => <MapperListWrapper value={value} onChange={onChange} {...props} />}
        rules={rules}
      />
      <ControllerError errors={errors} name={name} />
    </Flex>
  )
}

export { GenericMapperController }
